import type { components } from '../../lib/api/schema.gen'
import type { CartCsvRow } from './cart-csv'
import type { SkuResolveOutcome } from './sku-resolver'

type Sourceable = components['schemas']['Sourceable']

export interface ImportCatalogRowsResult {
  readonly addedCount: number
  readonly errors: string[]
}

/** Prefix a message with its 1-based source line, when known (a row from a
 * hand-built test fixture may have no `line`) — this is what lets
 * `groupRowErrors` later recognize and collapse several rows that failed for
 * the exact same reason. */
function withRow(line: number | undefined, message: string): string {
  return line === undefined ? message : `Row ${line}: ${message}`
}

const ROW_PATTERN = /^Row (\d+): (.+)$/
/** Matches the first double-quoted segment of a reason (almost always the
 * row's own SKU/part name) so two messages that differ ONLY in which part
 * they name are still recognized as "the same failure". */
const QUOTED_SEGMENT = /"[^"]*"/

/** A short, generic replacement for a reason once several rows share it —
 * the full sentence (often a verbatim server error) is fine for a ONE-OFF
 * row, but reads as noise repeated across many rows; this keeps the grouped
 * line compact instead. Checked in order, first match wins. */
const SHORT_REASON_RULES: ReadonlyArray<readonly [pattern: RegExp, short: string]> = [
  [/needs? a quote/i, 'need a quote before they can be added to the cart'],
  [/configurable part/i, 'are configurable parts — configure them before adding'],
  [/no part found/i, 'no matching parts found'],
]

function shortenReason(templateWithPlaceholder: string): string {
  const reason = templateWithPlaceholder
    .replace(QUOTED_SEGMENT, '')
    .replace(/\s{2,}/g, ' ')
    .trim()
  for (const [pattern, short] of SHORT_REASON_RULES) {
    if (pattern.test(reason)) return short
  }
  return reason
}

/**
 * Collapses several "Row N: <reason>" errors that share the exact same
 * reason (ignoring the quoted SKU/part name each one names) into one compact
 * "Rows N, M, …: <short reason>" line, rather than repeating the same
 * multi-sentence message once per affected row — e.g. six custom parts that
 * all need a quote before they can be added collapse to a single line
 * instead of six near-identical paragraphs. A reason that occurs only once
 * is left exactly as-is (no information loss for the common case). Anything
 * not shaped like "Row N: …" (e.g. a header-level parse error) passes
 * through untouched.
 */
function groupRowErrors(errors: readonly string[]): string[] {
  const matches = errors.map((e) => ROW_PATTERN.exec(e))
  const templateOf = (reason: string) => reason.replace(QUOTED_SEGMENT, ' ')

  const countByTemplate = new Map<string, number>()
  for (const m of matches) {
    if (m === null) continue
    const template = templateOf(m[2] as string)
    countByTemplate.set(template, (countByTemplate.get(template) ?? 0) + 1)
  }

  const emitted = new Set<string>()
  const output: string[] = []
  errors.forEach((error, index) => {
    const m = matches[index]
    if (!m) {
      output.push(error)
      return
    }
    const reason = m[2] as string
    const template = templateOf(reason)
    if ((countByTemplate.get(template) ?? 1) === 1) {
      output.push(error)
      return
    }
    if (emitted.has(template)) {
      return
    }
    emitted.add(template)
    const lines = matches
      .filter(
        (mm): mm is RegExpExecArray => mm !== null && templateOf(mm[2] as string) === template,
      )
      .map((mm) => Number(mm[1]))
    output.push(`Rows ${lines.join(', ')}: ${shortenReason(template)}`)
  })
  return output
}

export interface ImportCatalogRowsDeps {
  /**
   * Resolve a pasted/CSV row's part number across BOTH catalogs (MISUMI/partner
   * + Fictiv library) — the SAME shared {@link SkuResolveOutcome} the single
   * add-line box uses (`sku-resolver.ts`), replacing the old fixture-only
   * lookup. A `not-found`/`needs-config`/`error` outcome becomes a per-row
   * message rather than aborting the whole import.
   */
  readonly resolve: (partNumber: string) => Promise<SkuResolveOutcome>
  /** Add the resolved sourceable to the cart for this row's quantity. Each
   * caller supplies its own add path (and its own signature for it) — e.g.
   * `CartPage` merges through its page-local `handleAddLine` (which also
   * updates cart state), `HomePage` calls the standalone
   * `addCatalogResultToCart`. A thrown error here becomes a per-row
   * "Could not add" message rather than aborting the rest of the import. */
  readonly addLine: (sourceable: Sourceable, row: CartCsvRow) => Promise<unknown>
}

/**
 * Shared per-row resolve → add → error-collection loop behind Home's
 * "Copy from Excel" footer (UID-293) and Cart's bulk-import paths (CSV
 * upload + "Copy from Excel", UID-59) — both parse rows the same way
 * (`parseCartCsv`) and previously duplicated this exact loop.
 *
 * Each row is resolved across both sources; a resolved row is added, a
 * `needs-config` row is skipped with a "configure it first" note (mirroring the
 * single add-line box), and a `not-found`/`error` row collects a message.
 * Callers keep their own post-processing (toast/navigate for Home, an inline
 * `importErrors` banner for Cart) — this only returns the tally.
 */
export async function importCatalogRows(
  parsed: { readonly rows: readonly CartCsvRow[]; readonly errors: readonly string[] },
  deps: ImportCatalogRowsDeps,
): Promise<ImportCatalogRowsResult> {
  const errors: string[] = [...parsed.errors]
  let addedCount = 0

  for (const row of parsed.rows) {
    const outcome = await deps.resolve(row.sku)

    if (outcome.status === 'not-found') {
      errors.push(withRow(row.line, `No part found for "${row.sku}".`))
      continue
    }
    if (outcome.status === 'error') {
      errors.push(withRow(row.line, `Could not look up "${row.sku}": ${outcome.message}`))
      continue
    }
    if (outcome.status === 'needs-config') {
      errors.push(
        withRow(
          row.line,
          `"${row.sku}" is a configurable part — configure it in the catalog before adding.`,
        ),
      )
      continue
    }

    try {
      await deps.addLine(outcome.sourceable, row)
      addedCount += 1
    } catch (err) {
      const detail = err instanceof Error && err.message ? err.message : undefined
      errors.push(
        withRow(
          row.line,
          detail
            ? `Could not add "${row.sku}" to the cart: ${detail}`
            : `Could not add "${row.sku}" to the cart.`,
        ),
      )
    }
  }

  return { addedCount, errors: groupRowErrors(errors) }
}
